import React, { Fragment, useEffect, useState, useRef } from 'react';
import {
    Row,
    CardBody,
    Card,
    Button,
} from 'reactstrap';
import Select from 'react-select';
import { NotificationManager } from '../../../../../../components/common/react-notifications';
import { AudioPlayer } from './Player';
import { gAPI_ElevenLabsVoicesNew } from '../../../../../../constants/APIs';
import { AxiosAPI } from '../../../../../../helpers/AxiosAPI';
import Languages from '../../../../../../data/languages';
import CustomSelectInput from '../../../../../../components/common/CustomSelectInput';
import { Colxx } from '../../../../../../components/common/CustomBootstrap';

function VoiceAndBehaviour({ settings, saveIntegration }) {
    const [voices, setVoices] = useState([]);
    const [langFilter, setLangFilter] = useState(null);
    const [selectedVoiceId, setSelectedVoiceId] = useState(null);
    const [additionalLangs, setAdditionalLangs] = useState([]);
    const [features, setFeatures] = useState({
        recording: false,
        transcribe: false,
        voiceActor: '',
        autoLanguageDetection: false,
        realTimeStreaming: false,
        provider: '',
        voiceProcessing: 'Normal',
        agentId: '',
    });

    const isManualChangeRef = useRef(false);
    const voiceCacheRef = useRef({});
    useEffect(() => {
        if (!settings?.features) return;
        const f = settings.features;

        setFeatures({
            recording: f.recording,
            transcribe: f.transcribe,
            voiceActor: f.voiceActor,
            autoLanguageDetection: f.autoLanguageDetection,
            realTimeStreaming: f.realTimeStreaming,
            provider: f.provider || '',
            humanAssistant: f.humanAssistant || false,
            ES6Format: f.ES6Format || '',
            voiceProcessing: f.voiceProcessing,
            agentId: f.agentId || '',
        });

        const restoreFromSettings = async () => {
            const savedVoiceId = f.voiceActor;
            const voiceSettings = f.voiceSettings || [];
            const primaryLangCode = voiceSettings[0]?.language || null;
            const provider = f.provider === 'ElevenLabs' ? 0 : 1;

            try {
                const res = await AxiosAPI({
                    method: 'get',
                    url: gAPI_ElevenLabsVoicesNew,
                    params: {
                        provider,
                        gender: ' ',
                        language: primaryLangCode || ' ',
                    },
                });

                let list = res?.data?.voices || res?.data || [];
                if (!Array.isArray(list)) list = [];

                const mapped = list.map((v) => ({
                    id: v.provider_voice_id,
                    name: v.display_name,
                    language: v?.primary_language || '',
                    accent: v?.accent || '',
                    gender: v?.gender || '',
                    previewUrl: v.preview_url || '',
                    provider: v.provider,
                    // additional_languages: v.additional_languages
                    //     ? [...new Map(v.additional_languages.map((i) => [i.preview_url, i])).values()]
                    //     : [],
                }));

                setVoices(mapped);

                if (primaryLangCode) {
                    const langMatch = Languages.find((l) => l.code === primaryLangCode);
                    if (langMatch) {
                        setLangFilter({ value: langMatch.code, label: langMatch.name });
                    }
                }

                if (savedVoiceId) {
                    const voiceMatch = mapped.find((v) => v.id === savedVoiceId);
                    if (voiceMatch) {
                        setSelectedVoiceId(voiceMatch.id);
                    }
                }

                const additionals = voiceSettings.slice(1);
                if (additionals.length > 0) {
                    const rows = additionals.map((vs) => {
                        const langMatch = Languages.find((l) => l.code === vs.language);
                        return {
                            lang: langMatch
                                ? { value: langMatch.code, label: langMatch.name }
                                : null,
                            voice: vs.voiceActor
                                ? { value: vs.voiceActor, label: vs.name }
                                : null,
                            voices: [],
                        };
                    });
                    setAdditionalLangs(rows);
                }
            } catch {
                NotificationManager.error('Failed to load voices');
            }
        };

        restoreFromSettings();
    }, [settings]);

    useEffect(() => {
        if (!isManualChangeRef.current) return;

        const loadVoices = async () => {
            try {
                const res = await AxiosAPI({
                    method: 'get',
                    url: gAPI_ElevenLabsVoicesNew,
                    params: {
                        provider: features?.provider === 'ElevenLabs' ? 0 : 1,
                        gender: ' ',
                        language: langFilter?.value || ' ',
                    },
                });

                let list = res?.data?.voices || res?.data || [];
                if (!Array.isArray(list)) list = [];

                const mapped = list.map((v) => ({
                    id: v.provider_voice_id,
                    name: v.display_name,
                    language: v?.primary_language || '',
                    accent: v?.accent || '',
                    gender: v?.gender || '',
                    previewUrl: v.preview_url || '',
                }));

                setVoices(mapped);
            } catch {
                NotificationManager.error('Failed to load voices');
            }
        };

        loadVoices();
    }, [langFilter]);

    const filteredVoices = langFilter
        ? voices.filter((v) => v.language === langFilter.value)
        : voices;

    const selectedVoice = voices.find((v) => v.id === selectedVoiceId);

    const langOptions = Languages.map((l) => ({ value: l.code, label: l.name }));

    const updateAdditionalLang = async (i, field, val) => {
        if (field === 'lang') {
            const langCode = val?.value;

            try {
                let mapped = [];

                if (voiceCacheRef.current[langCode]) {
                    mapped = voiceCacheRef.current[langCode];
                } else {
                    const res = await AxiosAPI({
                        method: 'get',
                        url: gAPI_ElevenLabsVoicesNew,
                        params: {
                            provider: features?.provider === 'ElevenLabs' ? 0 : 1,
                            gender: ' ',
                            language: langCode || ' ',
                        },
                    });

                    let list = res?.data?.voices || res?.data || [];
                    if (!Array.isArray(list)) list = [];

                    mapped = list.map((v) => ({
                        value: v.provider_voice_id,
                        label: `${v.display_name}${v.accent ? ` (${v.accent})` : ''}`,
                        name: v.display_name,
                    }));

                    voiceCacheRef.current[langCode] = mapped;
                }

                setAdditionalLangs((prev) =>
                    prev.map((row, idx) =>
                        idx === i
                            ? { ...row, lang: val, voice: null, voices: mapped }
                            : row
                    )
                );
            } catch {
                NotificationManager.error('Failed to load voices');
            }

            return;
        }

        setAdditionalLangs((prev) =>
            prev.map((row, idx) =>
                idx === i ? { ...row, [field]: val } : row
            )
        );
    };

    const addAdditionalLang = () => {
        setAdditionalLangs((prev) => [
            ...prev,
            { lang: null, voice: null, voices: [] }
        ]);
    };

    const removeAdditionalLang = (i) => {
        setAdditionalLangs((prev) => prev.filter((_, idx) => idx !== i));
    };

    const handleSave = () => {
        const voiceSettings = [];

        if (selectedVoice) {
            voiceSettings.push({
                voiceActor: selectedVoice.id,
                language: selectedVoice.language,
                name: selectedVoice.name,
            });
        }

        additionalLangs.forEach(({ lang, voice }) => {
            if (lang && voice) {
                voiceSettings.push({
                    voiceActor: voice.value,
                    language: lang.value,
                    name: voice.name,
                });
            }
        });

        saveIntegration({
            features: {
                ...features,
                voiceSettings,
                voiceActor: selectedVoiceId,
                provider: 'ElevenLabs',
            },
        });
    };

    return (
        <Fragment>
            <Card>
                <CardBody style={{ padding: 28 }}>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111', marginBottom: 4 }}>
                        Voice &amp; Behaviour Settings
                    </h3>
                    <p>
                        Configure the voice actors and behaviour settings for your Voice AI. Select your
                        primary voice actor, filter by language, and add additional languages as needed.
                    </p>

                    {/*  Voice Actor (Primary)  */}
                    <div className="mt-2 mb-1" style={{ fontWeight: 600 }}>Voice Actor (Primary)</div>
                    <h6 style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
                        Choose the voice that best represents your brand and guest experience
                    </h6>

                    {/* Default Language selector */}
                    <div style={{ marginBottom: 14, maxWidth: 280 }}>
                        <Select
                            className="react-select"
                            classNamePrefix="react-select"
                            components={{ Input: CustomSelectInput }}
                            options={langOptions}
                            value={langFilter}
                            placeholder="Select default language..."
                            onChange={(val) => {
                                isManualChangeRef.current = true;
                                setLangFilter(val);
                                setSelectedVoiceId(null);
                            }}
                        />
                    </div>

                    {/* Scrollable voice card list */}
                    <div className="mt-4 voice-list">
                        {filteredVoices.length === 0 && (
                            <div style={{ textAlign: 'center', fontSize: 13, color: '#9ca3af', padding: '28px 0' }}>
                                {langFilter
                                    ? 'No voices available for this language.'
                                    : 'Select a default language to see available voices.'}
                            </div>
                        )}
                        {filteredVoices.map((v) => {
                            const isSelected = v.id === selectedVoiceId;
                            // console.log('selected', isSelected, v.id, selectedVoiceId);
                            return (
                                <div
                                    key={v.id}
                                    className={`voice-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => setSelectedVoiceId(v.id)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div className={`radio-circle ${isSelected ? 'selected' : ''}`}>
                                            {isSelected && <div className="radio-dot" />}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>
                                                {v.name}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
                                                {[v.gender, v.accent, v.language]
                                                    .filter(Boolean)
                                                    .map(str =>
                                                        typeof str === 'string'
                                                            ? str.charAt(0).toUpperCase() + str.slice(1)
                                                            : str
                                                    )
                                                    .join(' · ')}
                                            </div>
                                        </div>
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <AudioPlayer url={v.previewUrl} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <hr className="divider" />

                    {/* Languages section */}
                    <h4 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 4 }}>
                        Languages
                    </h4>
                    <p className="mb-2">
                        Choose the default and additional languages the agent will communicate in.
                    </p>

                    {/* Default language row */}
                    <div className="lang-row mb-2">
                        <span className="lang-badge">Default</span>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#111', flex: 1 }}>
                            {langFilter
                                ? langFilter.label
                                : <span style={{ color: '#9ca3af' }}>No default language selected</span>}
                        </span>
                        {selectedVoice && (
                            <p>Using {selectedVoice.name} </p>
                        )}
                    </div>

                    {/* Additional language rows */}
                    <div className="mt-3 ml-2">
                        {additionalLangs.map((row, i) => (
                            <Row key={i} className="mt-2 align-items-center">
                                <Colxx md={5}>
                                    <Select
                                        components={{ Input: CustomSelectInput }}
                                        className="react-select"
                                        classNamePrefix="react-select"
                                        options={langOptions}
                                        value={row.lang}
                                        onChange={(val) => updateAdditionalLang(i, 'lang', val)}
                                        placeholder="Language..."

                                    />
                                </Colxx>
                                <Colxx md={5}>
                                    <Select
                                        components={{ Input: CustomSelectInput }}
                                        className="react-select"
                                        classNamePrefix="react-select"

                                        placeholder={row.lang ? 'Select voice...' : 'Pick a language first'}
                                        options={row.voices || []}
                                        value={row.voice}
                                        onChange={(val) => updateAdditionalLang(i, 'voice', val)}
                                        isDisabled={!row.lang}
                                    />
                                </Colxx>
                                <Colxx md={2}>
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeAdditionalLang(i)}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </Colxx>
                            </Row>
                        ))}
                    </div>

                    {/* Add language button */}
                    <Button className="mt-4 ml-2" outline color="primary" onClick={addAdditionalLang}>
                        <svg
                            width="13" height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            style={{ marginRight: 4 }}
                        >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Additional Language
                    </Button>

                    <div className="mt-4 text-center">
                        <Button color="primary" onClick={handleSave}>
                            Save
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </Fragment>
    );
}

export default VoiceAndBehaviour;